import { CampaignTeamPicker } from "../components/CampaignTeamPicker";
import { DateField } from "../components/DateField";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Banner,
  Button,
  Card,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  Input,
  PageHead,
  PersonCell,
  ProgressBar,
  Skeleton,
  StatusPill,
  Textarea,
  useToast,
} from "@guestpilot/ui-kit";
import { useGet, useWrite } from "../api/hooks";
import type { PersonRef, Schema, Team } from "../api/types";
import { PersonPicker } from "../components/PersonPicker";
import { fieldErrorsOf, useHandleApiError } from "../errors";
import { formatDate } from "../format";
import { useI18n } from "../i18n";
import { useSession } from "../session";

type Campaign = Schema<"Campaign">;
type Preview = Schema<"CampaignPreview">;
type Settings = Schema<"Settings">;

const isoDay = (d: Date) => d.toISOString().slice(0, 10);

/** Review campaigns: past and active, with progress and outcomes; launch a new one (BR-070…BR-075). */
export function Campaigns() {
  const { t, locale } = useI18n();
  const s = useSession();
  const [params, setParams] = useSearchParams();
  const list = useGet<Campaign[]>("/api/v1/campaigns");
  const tz = s.me.tenant.timeZone;
  const open = params.get("new") === "1";
  return (
    <div className="gp-page">
      <PageHead
        title={t("ui:campaigns.title")}
        subtitle={t("ui:campaigns.subtitle")}
        actions={
          <Button
            variant="primary"
            disabledReason={s.gate("CAMPAIGN_LAUNCH")}
            onClick={() => setParams({ new: "1" })}
          >
            {t("ui:campaigns.new")}
          </Button>
        }
      />
      {list.isLoading ? <Skeleton rows={3} /> : null}
      {list.data && list.data.length === 0 ? <EmptyState title={t("ui:campaigns.empty")} /> : null}
      <div className="gp-stack" style={{ gap: "var(--space-16)" }}>
        {(list.data ?? []).map((c) => (
          <CampaignCard key={c.id} c={c} locale={locale} tz={tz} />
        ))}
      </div>
      <LaunchDialog open={open} presetTeam={params.get("team") ?? undefined} onClose={() => setParams({})} />
    </div>
  );
}

function CampaignCard({
  c,
  locale,
  tz,
}: {
  c: Campaign;
  locale: Parameters<typeof formatDate>[1];
  tz: string | undefined;
}) {
  const { t } = useI18n();
  const r = c.resultCounts ?? {};
  const teams = (c.teams ?? [])
    .map((x) => x.team?.displayName)
    .filter(Boolean)
    .join(", ");
  return (
    <Card
      title={<Link to={`/campaigns/${c.id}`}>{c.name}</Link>}
      actions={
        <StatusPill
          domain="campaign"
          value={c.status ?? "in_progress"}
          label={t(`ui:campaignStatus.${c.status}`)}
        />
      }
    >
      <p className="t-body gp-muted" style={{ margin: 0 }}>
        {t("ui:campaigns.by", { teams, owner: c.launchedBy?.displayName ?? "" })}
      </p>
      <div className="gp-row t-body-sm gp-muted">
        <span>{t("ui:campaigns.launched", { date: formatDate(c.launchedAt, locale, tz) })}</span>
        <span>{t("ui:campaigns.due", { date: formatDate(c.dueAt, locale, tz) })}</span>
        {c.completedAt ? (
          <span>{t("ui:campaigns.completed", { date: formatDate(c.completedAt, locale, tz) })}</span>
        ) : null}
      </div>
      <ProgressBar
        value={c.itemsResolved ?? 0}
        max={c.itemsTotal ?? 0}
        label={t("ui:campaigns.progress", { n: c.itemsResolved ?? 0, total: c.itemsTotal ?? 0 })}
      />
      <dl className="gp-grid-4" style={{ margin: 0 }}>
        {[
          [t("ui:campaigns.stat.reviewed"), `${c.itemsResolved ?? 0} / ${c.itemsTotal ?? 0}`],
          [t("ui:campaigns.stat.overdue"), `${c.overdueCount ?? 0}`],
          [t("ui:campaigns.stat.removal"), `${r.removalRequested ?? 0}`],
          [t("ui:campaigns.stat.extended"), `${r.extended ?? 0}`],
          [t("ui:campaigns.stat.exceptions"), `${r.exceptions ?? 0}`],
        ].map(([k, v]) => (
          <div key={k}>
            <dt className="t-body-sm gp-muted">{k}</dt>
            <dd className="t-title-sm" style={{ margin: 0 }}>
              {v}
            </dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

/** Create campaign: Teams checklist, live count of the review items to be created, structured reminder fields (personal-app.md). */
export function LaunchDialog({
  open,
  presetTeam,
  onClose,
}: {
  open: boolean;
  presetTeam?: string | undefined;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const nav = useNavigate();
  const fail = useHandleApiError();
  const teams = useGet<Team[]>("/api/v1/teams", { filter: "all" }, { enabled: open });
  const settings = useGet<Settings>("/api/v1/settings", undefined, { enabled: open });
  const preview = useWrite<Preview, { teamIds: string[] }>("POST", "/api/v1/campaigns/preview");
  const launch = useWrite<Campaign, Schema<"CampaignCreate">>("POST", "/api/v1/campaigns", [
    "/api/v1/campaigns",
    "/api/v1/teams",
    "/api/v1/dashboard",
    "/api/v1/onboarding",
  ]);
  const [name, setName] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [due, setDue] = useState("");
  const [before, setBefore] = useState<number | undefined>();
  const [repeat, setRepeat] = useState<number | undefined>();
  const [instructions, setInstructions] = useState<string | undefined>();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const st = settings.data;

  useEffect(() => {
    if (open && presetTeam) setPicked(new Set([presetTeam]));
  }, [open, presetTeam]);
  useEffect(() => {
    if (open && st && !due)
      setDue(isoDay(new Date(Date.now() + (st.defaultCampaignWindowDays ?? 14) * 86_400_000)));
  }, [open, st, due]);
  // nothing to review in a Team without external users, and a Team already in an open campaign cannot be chosen again
  const eligible = (teams.data ?? []).filter((x) => x.status === "active" && (x.externalUserCount ?? 0) > 0);
  const selectable = useMemo(
    () => new Set(eligible.filter((x) => !x.activeCampaignId).map((x) => x.id)),
    [teams.data],
  );
  const ids = useMemo(() => [...picked].filter((id) => selectable.has(id)).sort(), [picked, selectable]);
  useEffect(() => {
    if (ids.length > 0) preview.mutate({ body: { teamIds: ids } });
    // the preview follows the checklist
  }, [ids.join(",")]);

  const total = preview.data?.totalItems ?? 0;
  const reviewers = [
    ...new Set((preview.data?.perTeam ?? []).flatMap((p) => (p.reviewers ?? []).map((r) => r.displayName))),
  ];
  const skipped = (preview.data?.perTeam ?? []).filter((p) => p.skippedReason);

  const submit = () =>
    launch.mutate(
      {
        body: {
          name: name.trim(),
          teamIds: ids,
          dueAt: new Date(`${due}T23:59:59Z`).toISOString(),
          instructions: instructions ?? st?.defaultCampaignInstructions ?? "",
          reminderBeforeDays: before ?? st?.reminderBeforeDays ?? 3,
          reminderRepeatDays: repeat ?? st?.reminderRepeatDays ?? 2,
        },
      },
      {
        onSuccess: (c) => {
          nav(`/campaigns/${c.id ?? ""}`); // leaving the page closes the dialog
        },
        onError: (e) => {
          setErrors(fieldErrorsOf(e));
          fail(e);
        },
      },
    );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title={t("ui:launch.title")}
      description={t("ui:launch.lead")}
      footer={
        <>
          <Button onClick={onClose}>{t("common:cancel")}</Button>
          <Button
            variant="primary"
            disabledReason={
              !name.trim()
                ? t("ui:launch.nameRequired")
                : ids.length === 0
                  ? t("ui:launch.pickTeam")
                  : total === 0
                    ? t("ui:launch.nothing")
                    : undefined
            }
            onClick={submit}
          >
            {t("ui:launch.action")}
          </Button>
        </>
      }
    >
      <Field label={t("ui:launch.name")} error={errors["name"]}>
        <Input value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <CampaignTeamPicker
        teams={eligible}
        picked={new Set(ids)}
        onChange={setPicked}
        onOpenCampaign={(id) => nav(`/campaigns/${id}`)}
      />
      <div className="gp-launch-summary" role="status" aria-live="polite" aria-busy={preview.isPending}>
        {ids.length === 0 ? (
          <span className="t-body gp-muted">{t("ui:launch.pickTeam")}</span>
        ) : (
          <>
            <div>
              <span className="t-title-sm">{ids.length}</span>
              <span className="t-body-sm gp-muted">{t("ui:launch.sumTeams", { n: ids.length })}</span>
            </div>
            <div>
              <span className="t-title-sm">{total}</span>
              <span className="t-body-sm gp-muted">{t("ui:launch.sumItems", { n: total })}</span>
            </div>
            <div>
              <span className="t-title-sm">{reviewers.length}</span>
              <span className="t-body-sm gp-muted">
                {t("ui:launch.sumReviewers", { n: reviewers.length })}
              </span>
            </div>
          </>
        )}
      </div>
      {ids.length > 0 ? (
        <p className="t-body-sm gp-muted" style={{ margin: 0 }}>
          {t("ui:launch.hint", { n: total, owners: reviewers.join(", ") || "—" })}
        </p>
      ) : null}
      {skipped.map((p) => (
        <Banner key={p.teamId} tone="warn">
          {t(`ui:launch.skipped.${p.skippedReason}`, {
            team: (teams.data ?? []).find((x) => x.id === p.teamId)?.displayName ?? "",
          })}
        </Banner>
      ))}
      <h3 className="t-body-strong gp-launch-sub">{t("ui:launch.planning")}</h3>
      <div className="gp-grid-3">
        <Field label={t("ui:launch.due")} error={errors["dueAt"]}>
          <DateField value={due} onChange={(e) => setDue(e.target.value)} />
        </Field>
        <Field label={t("ui:launch.before")} help={t("ui:launch.beforeHelp")}>
          <Input
            type="number"
            min={0}
            value={before ?? st?.reminderBeforeDays ?? 3}
            onChange={(e) => setBefore(Number(e.target.value))}
          />
        </Field>
        <Field label={t("ui:launch.repeat")} help={t("ui:launch.repeatHelp")}>
          <Input
            type="number"
            min={1}
            value={repeat ?? st?.reminderRepeatDays ?? 2}
            onChange={(e) => setRepeat(Number(e.target.value))}
          />
        </Field>
      </div>
      <Field label={t("ui:launch.instructions")}>
        <Textarea
          value={instructions ?? st?.defaultCampaignInstructions ?? ""}
          onChange={(e) => setInstructions(e.target.value)}
        />
      </Field>
    </Dialog>
  );
}

/** Manage one campaign: progress per Team, extend the due date, reassign reviewers, close as incomplete or cancel (BR-209). */
export function CampaignDetail() {
  const { campaignId = "" } = useParams();
  const { t, locale } = useI18n();
  const s = useSession();
  const toast = useToast();
  const fail = useHandleApiError();
  const tz = s.me.tenant.timeZone;
  const q = useGet<Campaign>(`/api/v1/campaigns/${campaignId}`);
  const invalidate = ["/api/v1/campaigns", "/api/v1/teams", "/api/v1/dashboard"];
  const patch = useWrite<Campaign, Schema<"CampaignUpdate">>("PATCH", "/api/v1/campaigns/:id", invalidate);
  const close = useWrite<Campaign, Schema<"CampaignClose">>(
    "POST",
    "/api/v1/campaigns/:id/close",
    invalidate,
  );
  const cancel = useWrite<Campaign>("POST", "/api/v1/campaigns/:id/cancel", invalidate);
  const reassign = useWrite<Campaign, Schema<"CampaignReassign">>(
    "POST",
    "/api/v1/campaigns/:id/reassign",
    invalidate,
  );
  const [due, setDue] = useState("");
  const [confirm, setConfirm] = useState<"close" | "cancel" | null>(null);
  const [reassignTeam, setReassignTeam] = useState<string | null>(null);
  const [picked, setPicked] = useState<PersonRef | null>(null);
  if (q.isLoading) return <Skeleton rows={5} />;
  const c = q.data;
  if (!c) return <EmptyState title={t("errors:not_found")} />;
  const running = c.status === "in_progress";
  const gate = running ? s.gate("CAMPAIGN_MANAGE") : t("ui:campaigns.closedGate");
  return (
    <div className="gp-page">
      <PageHead
        title={c.name ?? ""}
        subtitle={t("ui:campaigns.by", {
          teams: (c.teams ?? []).map((x) => x.team?.displayName).join(", "),
          owner: c.launchedBy?.displayName ?? "",
        })}
        actions={
          <StatusPill
            domain="campaign"
            value={c.status ?? "in_progress"}
            label={t(`ui:campaignStatus.${c.status}`)}
          />
        }
      />
      <Card title={t("ui:campaigns.manage")}>
        <ProgressBar
          value={c.itemsResolved ?? 0}
          max={c.itemsTotal ?? 0}
          label={t("ui:campaigns.progress", { n: c.itemsResolved ?? 0, total: c.itemsTotal ?? 0 })}
        />
        <div className="gp-row">
          <Field label={t("ui:campaigns.extendLabel", { date: formatDate(c.dueAt, locale, tz) })}>
            <DateField value={due} onChange={(e) => setDue(e.target.value)} />
          </Field>
          <Button
            disabledReason={gate ?? (due ? undefined : t("ui:campaigns.pickDate"))}
            onClick={() =>
              patch.mutate(
                {
                  params: { id: campaignId },
                  body: { version: c.version ?? 0, dueAt: new Date(`${due}T23:59:59Z`).toISOString() },
                },
                { onSuccess: () => toast.success(t("ui:campaigns.extended")), onError: fail },
              )
            }
          >
            {t("ui:campaigns.extend")}
          </Button>
        </div>
        <div className="gp-actions">
          <Button disabledReason={gate} onClick={() => setConfirm("close")}>
            {t("ui:campaigns.close")}
          </Button>
          <Button variant="danger" disabledReason={gate} onClick={() => setConfirm("cancel")}>
            {t("ui:campaigns.cancel")}
          </Button>
        </div>
      </Card>
      <Card title={t("ui:campaigns.perTeam")}>
        <DataTable
          caption={t("ui:campaigns.perTeam")}
          rows={c.teams ?? []}
          rowKey={(x) => x.team?.id ?? ""}
          emptyTitle="-"
          columns={[
            { key: "team", header: t("ui:users.col.team"), render: (x) => x.team?.displayName },
            {
              key: "reviewers",
              header: t("ui:campaigns.reviewers"),
              render: (x) =>
                (x.reviewers ?? []).length
                  ? (x.reviewers ?? []).map((r) => (
                      <PersonCell key={r.id} name={r.displayName} avatar={false} />
                    ))
                  : "—",
            },
            {
              key: "progress",
              header: t("ui:campaigns.stat.reviewed"),
              numeric: true,
              render: (x) => `${x.itemsResolved ?? 0} / ${x.itemsTotal ?? 0}`,
            },
            {
              key: "status",
              header: t("ui:users.col.status"),
              render: (x) => (
                <StatusPill
                  domain="campaign"
                  value={x.status ?? "in_progress"}
                  label={t(`ui:campaignStatus.${x.status}`)}
                />
              ),
            },
            {
              key: "act",
              header: t("ui:table.actions"),
              render: (x) => (
                <Button
                  variant="ghost"
                  disabledReason={gate}
                  onClick={() => setReassignTeam(x.team?.id ?? null)}
                >
                  {t("ui:campaigns.reassign")}
                </Button>
              ),
            },
          ]}
        />
      </Card>
      <Dialog
        open={confirm !== null}
        onClose={() => setConfirm(null)}
        title={confirm === "close" ? t("ui:campaigns.closeTitle") : t("ui:campaigns.cancelTitle")}
        description={confirm === "close" ? t("ui:campaigns.closeBody") : t("ui:campaigns.cancelBody")}
        footer={
          <>
            <Button onClick={() => setConfirm(null)}>{t("common:cancel")}</Button>
            <Button
              variant="danger"
              onClick={() =>
                (confirm === "close" ? close : cancel).mutate(
                  { params: { id: campaignId }, body: {} },
                  { onSuccess: () => setConfirm(null), onError: fail },
                )
              }
            >
              {t("common:confirm")}
            </Button>
          </>
        }
      />
      <Dialog
        open={reassignTeam !== null}
        onClose={() => setReassignTeam(null)}
        title={t("ui:campaigns.reassign")}
        description={t("ui:campaigns.reassignLead")}
        footer={
          <>
            <Button onClick={() => setReassignTeam(null)}>{t("common:cancel")}</Button>
            <Button
              variant="primary"
              disabledReason={picked ? undefined : t("ui:delegate.pick")}
              onClick={() =>
                reassign.mutate(
                  {
                    params: { id: campaignId },
                    body: { teamId: reassignTeam ?? "", reviewerPersonIds: [picked!.id] },
                  },
                  {
                    onSuccess: () => {
                      setReassignTeam(null);
                      setPicked(null);
                    },
                    onError: fail,
                  },
                )
              }
            >
              {t("ui:campaigns.reassign")}
            </Button>
          </>
        }
      >
        <PersonPicker label={t("ui:campaigns.reviewers")} value={picked} onChange={setPicked} />
      </Dialog>
    </div>
  );
}
